/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { AiOutlineExport, AiOutlineFileExcel } from "react-icons/ai";
import { FaFileCsv, FaFileAlt } from "react-icons/fa";

export interface ExportColumn {
  key: string;
  label: string;
  width?: number;
  format?: (value: any) => any;
  excludeFromExport?: boolean;
}

export interface ExportConfig {
  data: any[];
  columns: ExportColumn[];
  filename?: string;
  sheetName?: string;
  formats?: ("xlsx" | "csv" | "json" | "txt")[];
  styling?: {
    headerColor?: string;
    headerFontColor?: string;
    headerFontSize?: number;
    dataFontSize?: number;
    addBorders?: boolean;
    alternateRowColor?: boolean;
  };
  onExportStart?: () => void;
  onExportComplete?: () => void;
  onExportError?: (error: Error) => void;
}

interface ExportProps {
  config: ExportConfig;
  buttonText?: string;
  buttonClassName?: string;
  showFormatSelector?: boolean;
  variant?: "button" | "dropdown" | "icon";
  size?: "sm" | "md" | "lg";
}

const Export: React.FC<ExportProps> = ({
  config,
  buttonText = "Export",
  buttonClassName = "",
  showFormatSelector = true,
  variant = "dropdown",
  size = "md",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedFormat = "xlsx";

  const {
    data,
    columns,
    filename = "export",
    sheetName = "Sheet1",
    formats = ["xlsx", "csv", "json"],
    styling = {},
    onExportStart,
    onExportComplete,
    onExportError,
  } = config;

  // Default styling options
  const defaultStyling = {
    headerColor: "4472C4",
    headerFontColor: "FFFFFF",
    headerFontSize: 14,
    dataFontSize: 11,
    addBorders: true,
    alternateRowColor: true,
    ...styling,
  };

  // Filter out excluded columns
  const exportColumns = columns.filter((col) => !col.excludeFromExport);

  // Helper function to get nested property value using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Prepare data for export
  const prepareData = () => {
    return data.map((row) => {
      const exportRow: any = {};
      exportColumns.forEach((col) => {
        const value = getNestedValue(row, col.key);
        exportRow[col.label] = col.format ? col.format(value) : value;
      });
      return exportRow;
    });
  };

  const getHeaderBorder = () => {
    if (!defaultStyling.addBorders) return undefined;
    return {
      top: { style: "medium", color: { rgb: "000000" } },
      bottom: { style: "medium", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };
  };

  const getDataBorder = () => {
    if (!defaultStyling.addBorders) return undefined;
    return {
      top: { style: "thin", color: { rgb: "D3D3D3" } },
      bottom: { style: "thin", color: { rgb: "D3D3D3" } },
      left: { style: "thin", color: { rgb: "D3D3D3" } },
      right: { style: "thin", color: { rgb: "D3D3D3" } },
    };
  };

  const getHeaderStyle = () => ({
    font: {
      bold: true,
      sz: defaultStyling.headerFontSize,
      color: { rgb: defaultStyling.headerFontColor },
    },
    fill: { fgColor: { rgb: defaultStyling.headerColor } },
    alignment: { horizontal: "center", vertical: "center" },
    border: getHeaderBorder(),
  });

  const getDataStyle = (rowNumber: number) => ({
    font: { sz: defaultStyling.dataFontSize },
    fill:
      defaultStyling.alternateRowColor && rowNumber % 2 === 0
        ? { fgColor: { rgb: "F2F2F2" } }
        : undefined,
    border: getDataBorder(),
  });

  const styleHeaderRow = (worksheet: XLSX.WorkSheet, range: XLSX.Range) => {
    for (let col = range.s.c; col <= range.e.c; ++col) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = getHeaderStyle();
    }
  };

  const styleDataRows = (worksheet: XLSX.WorkSheet, range: XLSX.Range) => {
    for (let row = range.s.r + 1; row <= range.e.r; ++row) {
      for (let col = range.s.c; col <= range.e.c; ++col) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: "" };
        worksheet[cellAddress].s = getDataStyle(row);
      }
    }
  };

  const applyWorksheetStyling = (worksheet: XLSX.WorkSheet) => {
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
    styleHeaderRow(worksheet, range);
    styleDataRows(worksheet, range);
    worksheet["!cols"] = exportColumns.map((col) => ({ wch: col.width || 15 }));
    worksheet["!rows"] = [{ hpt: 25 }];
  };

  // Export to Excel with styling
  const exportToExcel = () => {
    try {
      onExportStart?.();

      const exportData = prepareData();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();

      applyWorksheetStyling(ws);

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${filename}.xlsx`);

      onExportComplete?.();
    } catch (error) {
      onExportError?.(error as Error);
      console.error("Export error:", error);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      if (onExportStart) onExportStart();

      const exportData = prepareData();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `${filename}.csv`);

      if (onExportComplete) onExportComplete();
    } catch (error) {
      if (onExportError) onExportError(error as Error);
      console.error("Export error:", error);
    }
  };

  // Export to JSON
  const exportToJSON = () => {
    try {
      if (onExportStart) onExportStart();

      const exportData = prepareData();
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      saveAs(blob, `${filename}.json`);

      if (onExportComplete) onExportComplete();
    } catch (error) {
      if (onExportError) onExportError(error as Error);
      console.error("Export error:", error);
    }
  };

  // Export to TXT
  const exportToTXT = () => {
    try {
      if (onExportStart) onExportStart();

      const exportData = prepareData();
      let txt = exportColumns.map((col) => col.label).join("\t") + "\n";
      exportData.forEach((row) => {
        txt += Object.values(row).join("\t") + "\n";
      });
      const blob = new Blob([txt], { type: "text/plain;charset=utf-8;" });
      saveAs(blob, `${filename}.txt`);

      if (onExportComplete) onExportComplete();
    } catch (error) {
      if (onExportError) onExportError(error as Error);
      console.error("Export error:", error);
    }
  };

  const handleExport = (format: string) => {
    switch (format) {
      case "xlsx":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      case "json":
        exportToJSON();
        break;
      case "txt":
        exportToTXT();
        break;
      default:
        exportToExcel();
    }
    setIsOpen(false);
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case "xlsx":
        return <AiOutlineFileExcel className="inline mr-2" />;
      case "csv":
        return <FaFileCsv className="inline mr-2" />;
      case "json":
        return <FaFileAlt className="inline mr-2" />;
      case "txt":
        return <FaFileAlt className="inline mr-2" />;
      default:
        return <AiOutlineExport className="inline mr-2" />;
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "text-xs px-3 py-1.5";
      case "lg":
        return "text-base px-6 py-3";
      default:
        return "text-sm px-5 py-2.5";
    }
  };

  // Icon variant
  if (variant === "icon") {
    return (
      <div className="relative inline-block">
        <button
          onClick={() => handleExport(selectedFormat)}
          className={`inline-flex items-center p-2 text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 ${buttonClassName}`}
          title="Export"
        >
          <AiOutlineExport size={20} />
        </button>
      </div>
    );
  }

  // Button variant (single format)
  if (variant === "button" || !showFormatSelector) {
    return (
      <button
        onClick={() => handleExport(selectedFormat)}
        className={`inline-flex items-center ${getSizeClasses()} text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 ${buttonClassName}`}
      >
        <AiOutlineExport size={18} className="mr-2" />
        {buttonText}
      </button>
    );
  }

  // Dropdown variant (multiple formats)
  return (
    <div className="relative inline-block pr-1.5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center ${getSizeClasses()} text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 ${buttonClassName}`}
      >
        <AiOutlineExport size={18} className="mr-2" />
        {buttonText}
        <svg
          className={`ml-2 w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="absolute right-0 z-50 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-600">
            <ul className="py-1">
              {formats.map((format) => (
                <li key={format}>
                  <button
                    onClick={() => handleExport(format)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center"
                  >
                    {getFormatIcon(format)}
                    Export as {format.toUpperCase()}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default Export;
